import { useCallback } from "react";
import { SecretClipboard } from "../lib/clipboard.js";
import type { CopyResult } from "../lib/clipboard.js";
import { useToast } from "../ui/Toast.js";
import { getClipboardSeconds } from "./preferences.js";

/** App-wide secret clipboard: every copy restarts the wipe window from the user's preference. */
export const secretClipboard = new SecretClipboard({
  clipboard: typeof navigator !== "undefined" ? navigator.clipboard : undefined,
  clearAfterMs: (): number => getClipboardSeconds() * 1000,
  schedule: (fn: () => void, ms: number): number => window.setTimeout(fn, ms),
  cancel: (id: number): void => window.clearTimeout(id)
});

export function copyMessage(result: CopyResult, what: string = "Kopiert"): { readonly text: string; readonly tone: "success" | "danger" } {
  if (result === "copied") {
    return { text: `${what}. Die Zwischenablage wird in ${Math.round(secretClipboard.clearAfterMs / 1000)} Sekunden geleert.`, tone: "success" };
  }
  if (result === "unavailable") {
    return { text: "Zwischenablage ist hier nicht verfügbar. Bitte über HTTPS öffnen.", tone: "danger" };
  }
  return { text: "Kopieren fehlgeschlagen.", tone: "danger" };
}

/** Copies a secret and reports the outcome as a toast. Resolves to whether the copy worked. */
export function useSecretCopy(): (text: string, what?: string) => Promise<boolean> {
  const toast = useToast();
  return useCallback(
    async (text: string, what?: string): Promise<boolean> => {
      const result = await secretClipboard.copy(text);
      const message = copyMessage(result, what);
      toast.notify(message.text, { tone: message.tone });
      return result === "copied";
    },
    [toast]
  );
}
